import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Upload, Zap, Download, CheckCircle2, Loader2, FileText, Tag, TrendingUp } from 'lucide-react';
import { ChatAssistant } from '@/components/ChatAssistant';
import { HelpCenter } from '@/components/HelpCenter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Workspace = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [labeling, setLabeling] = useState(false);
  const [customInstructions, setCustomInstructions] = useState('');
  const [labelingMode, setLabelingMode] = useState('classification');
  const [editingItem, setEditingItem] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProject();
    fetchItems();
    fetchStats();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error('Failed to load project');
    }
  };

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/items`);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const response = await axios.post(
        `${API}/projects/${projectId}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(response.data.message);
      fetchItems();
      fetchStats();
      fetchProject();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAutoLabel = async () => {
    setLabeling(true);
    try {
      const response = await axios.post(`${API}/label`, {
        project_id: projectId,
        labeling_mode: labelingMode,
        custom_instructions: customInstructions || null
      });
      toast.success(response.data.message);
      fetchItems();
      fetchStats();
      fetchProject();
    } catch (error) {
      console.error('Error auto-labeling:', error);
      toast.error(error.response?.data?.detail || 'Failed to auto-label');
    } finally {
      setLabeling(false);
    }
  };

  const handleUpdateLabel = async (itemId, newLabel) => {
    try {
      await axios.put(`${API}/items/${itemId}/label`, {
        label: newLabel,
        reviewed: true
      });
      toast.success('Label updated');
      fetchItems();
      fetchStats();
      setEditingItem(null);
    } catch (error) {
      console.error('Error updating label:', error);
      toast.error('Failed to update label');
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/export?format=${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `project_${projectId}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export data');
    }
  };

  if (!project || !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
      {/* Modern Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate('/')}
                className="rounded-full hover:bg-gray-100"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
                <p className="text-sm text-muted-foreground">{project.description || 'No description'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleExport('json')}
                className="rounded-full border-2 hover:bg-blue-50 hover:border-blue-600 hover:text-blue-700"
                data-testid="export-json-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                JSON
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleExport('csv')}
                className="rounded-full border-2 hover:bg-blue-50 hover:border-blue-600 hover:text-blue-700"
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                CSV
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Workspace */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-12 gap-8">
          {/* Left Sidebar - Control Panel */}
          <div className="col-span-12 lg:col-span-4 space-y-6">
            {/* Statistics Card */}
            <Card className="border-0 shadow-md bg-white">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-6">Project Statistics</h3>
                
                {/* Progress */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Progress</span>
                    <span className="text-sm font-bold text-blue-700">{Math.round(stats.completion_rate)}%</span>
                  </div>
                  <Progress value={stats.completion_rate} className="h-2.5" />
                </div>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-4 text-center">
                    <div className="text-3xl font-bold text-blue-700">{stats.total_items}</div>
                    <div className="text-xs font-medium text-gray-600 mt-1">Total Items</div>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-50 to-slate-50 rounded-2xl p-4 text-center">
                    <div className="text-3xl font-bold text-emerald-600">{stats.labeled_items}</div>
                    <div className="text-xs font-medium text-gray-600 mt-1">Labeled</div>
                  </div>
                </div>
                
                {/* Additional Stats */}
                <div className="mt-6 pt-6 border-t space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Unlabeled</span>
                    <span className="font-semibold text-gray-900">{stats.unlabeled_items}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">AI Generated</span>
                    <span className="font-semibold text-gray-900">{stats.ai_generated}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Reviewed</span>
                    <span className="font-semibold text-gray-900">{stats.reviewed_items}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Upload Data Card */}
            <Card className="border-0 shadow-md bg-white">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Upload Data</h3>
                <div 
                  className="upload-zone border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center cursor-pointer hover:border-blue-600 transition-all"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-zone"
                >
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-slate-100 flex items-center justify-center mx-auto mb-4">
                    <Upload className="w-8 h-8 text-blue-700" />
                  </div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">Click to upload</p>
                  <p className="text-xs text-gray-500">TXT or CSV files</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".txt,.csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-input"
                  />
                </div>
                {uploading && (
                  <div className="mt-4 flex items-center justify-center gap-2 text-sm text-blue-700">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="font-medium">Uploading...</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* AI Labeling Card */}
            <Card className="border-0 shadow-md bg-gradient-to-br from-blue-50 to-slate-50">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-blue-700" />
                  AI Auto-Label
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-semibold text-gray-700 mb-2 block">Labeling Mode</Label>
                    <Select value={labelingMode} onValueChange={setLabelingMode}>
                      <SelectTrigger className="w-full bg-white border-gray-200" data-testid="labeling-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white max-h-[400px] overflow-y-auto">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Basic</div>
                        <SelectItem value="classification">📋 Classification</SelectItem>
                        <SelectItem value="topic">📚 Topic Modeling</SelectItem>
                        <SelectItem value="keywords">🔑 Keywords</SelectItem>
                        <SelectItem value="summary">📝 Summarization</SelectItem>
                        
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase mt-2">Sentiment & Emotion</div>
                        <SelectItem value="sentiment">😊 Sentiment</SelectItem>
                        <SelectItem value="emotion">💭 Emotion</SelectItem>
                        
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase mt-2">Extraction</div>
                        <SelectItem value="ner">🏷️ Named Entities</SelectItem>
                        <SelectItem value="pii">🔒 PII Detection</SelectItem>
                        
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase mt-2">Intent & Purpose</div>
                        <SelectItem value="intent">🎯 Intent</SelectItem>
                        <SelectItem value="question_answer">❓ Q&A Generation</SelectItem>
                        
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase mt-2">Moderation</div>
                        <SelectItem value="toxicity">⚠️ Toxicity</SelectItem>
                        <SelectItem value="spam">🚫 Spam</SelectItem>
                        
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase mt-2">Properties</div>
                        <SelectItem value="language">🌍 Language</SelectItem>
                        <SelectItem value="formality">👔 Formality</SelectItem>
                        <SelectItem value="urgency">⏰ Urgency</SelectItem>
                        
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase mt-2">Advanced</div>
                        <SelectItem value="custom">⚙️ Custom</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-600 mt-2">
                      {labelingMode === 'classification' && 'Assign categories to text'}
                      {labelingMode === 'ner' && 'Extract entities (Person, Org, Location)'}
                      {labelingMode === 'sentiment' && 'Positive/Negative/Neutral'}
                      {labelingMode === 'emotion' && 'Joy, Sadness, Anger, Fear, etc.'}
                      {labelingMode === 'topic' && 'Identify main topics'}
                      {labelingMode === 'custom' && 'Use custom instructions below'}
                      {!['classification', 'ner', 'sentiment', 'emotion', 'topic', 'custom'].includes(labelingMode) && 'AI-powered analysis'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-gray-700 mb-2 block">Custom Instructions (Optional)</Label>
                    <Textarea
                      value={customInstructions}
                      onChange={(e) => setCustomInstructions(e.target.value)}
                      placeholder="E.g., Categorize as: Positive, Negative, Neutral"
                      className="bg-white border-gray-200 text-sm"
                      rows={3}
                      data-testid="custom-instructions-input"
                    />
                  </div>
                  <Button 
                    onClick={handleAutoLabel}
                    disabled={labeling || stats.unlabeled_items === 0}
                    className="w-full btn-modern h-11 rounded-full"
                    data-testid="auto-label-btn"
                  >
                    {labeling ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Labeling...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Label All ({stats.unlabeled_items})
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Content - Items List */}
          <div className="col-span-12 lg:col-span-8">
            <Card className="border-0 shadow-md bg-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Data Items</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {items.length} items • {stats.labeled_items} labeled • {stats.unlabeled_items} remaining
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>

                {items.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20" data-testid="empty-items-state">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mb-6">
                      <FileText className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No Data Yet</h3>
                    <p className="text-gray-600 text-center max-w-md">Upload a file to start labeling your data with AI assistance</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {items.map((item) => (
                      <div key={item.id} className="item-row bg-white border border-gray-200 rounded-2xl p-4 hover:shadow-md transition-all" data-testid={`item-${item.id}`}>
                        <div className="flex items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-700 mb-3 leading-relaxed">{item.text}</p>
                            <div className="flex items-center gap-2 flex-wrap">
                              {item.label ? (
                                editingItem === item.id ? (
                                  <div className="flex items-center gap-2">
                                    <Input
                                      defaultValue={item.label}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          handleUpdateLabel(item.id, e.target.value);
                                        }
                                      }}
                                      className="w-48 h-9"
                                      data-testid={`edit-label-input-${item.id}`}
                                    />
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => setEditingItem(null)}
                                      className="h-9 rounded-full"
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                ) : (
                                  <>
                                    <span className="label-badge inline-flex items-center gap-1">
                                      <Tag className="w-3 h-3" />
                                      {item.label}
                                    </span>
                                    {item.ai_generated && (
                                      <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                                        AI
                                      </span>
                                    )}
                                    {item.reviewed && (
                                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                    )}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setEditingItem(item.id)}
                                      className="h-8 text-xs rounded-full hover:bg-gray-100"
                                      data-testid={`edit-label-btn-${item.id}`}
                                    >
                                      Edit
                                    </Button>
                                  </>
                                )
                              ) : (
                                <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">
                                  Unlabeled
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Help Center */}
      <HelpCenter />
      
      {/* AI Chat Assistant */}
      <ChatAssistant />
    </div>
  );
};

export default Workspace;